import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { errorMessageFor } from '../auth/error-message';
import { PageState } from '../components/PageState';
import { BarChart } from '../training/charts/BarChart';
import { LineChart } from '../training/charts/LineChart';
import { formatDate, formatKg } from '../lib/format';
import { createTrainingApi } from '../training/training-api';
import './member.css';
import './train-progress.css';

const trainingApi = createTrainingApi();

// `formatDate` already turns a DATEONLY string into `DD/MM/AAAA` without
// touching the value; this only trims it to `DD/MM` for chart x-axis labels
// (string slicing, not a date computation — the server remains the sole
// authority on which week/day a session belongs to).
function shortDate(value) {
  const full = formatDate(value);
  return full ? full.slice(0, 5) : '';
}

/**
 * `/mi-cuenta/progreso` (spec "Progreso", plan Task 9): every number here —
 * the summary cards, both `BarChart`s and the per-exercise `LineChart` — is
 * exactly what `GET /me/progress` / `GET /me/progress/exercises/:id` sent;
 * this page never computes a metric itself (spec: "el cliente no calcula
 * métricas"), only formats dates for display.
 *
 * The exercise picker for the per-exercise chart is the member's own
 * personal-record list: `getProgress` doesn't expose a general "exercises
 * I've trained" endpoint, and every PR is, by definition, an exercise the
 * member has a completed history for — exactly the set `getExerciseProgress`
 * can answer for. Selecting one fetches its `points`/`best`/`last`.
 */
export function ProgressPage({ api = trainingApi }) {
  const { handleApiError } = useAuth();
  const [state, setState] = useState({ status: 'loading', data: null, error: '' });
  const [selectedExerciseId, setSelectedExerciseId] = useState(null);
  const [exerciseState, setExerciseState] = useState({ status: 'idle', data: null, error: '' });

  const load = useCallback(async () => {
    setState({ status: 'loading', data: null, error: '' });
    try {
      const data = await api.getMyProgress();
      setState({ status: 'loaded', data, error: '' });
    } catch (caught) {
      const outcome = handleApiError(caught, {
        clearBusinessData: () => setState({ status: 'loading', data: null, error: '' }),
      });
      if (outcome.handled) return;
      setState({ status: 'error', data: null, error: errorMessageFor(caught) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, handleApiError]);

  useEffect(() => {
    load();
  }, [load]);

  const selectExercise = useCallback(async (exerciseId) => {
    setSelectedExerciseId(exerciseId);
    setExerciseState({ status: 'loading', data: null, error: '' });
    try {
      const data = await api.getMyExerciseProgress(exerciseId);
      setExerciseState({ status: 'loaded', data, error: '' });
    } catch (caught) {
      const outcome = handleApiError(caught, {});
      if (outcome.handled) return;
      setExerciseState({ status: 'error', data: null, error: errorMessageFor(caught) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, handleApiError]);

  if (state.status === 'loading') {
    return <PageState title="Progreso" status="loading" />;
  }

  if (state.status === 'error') {
    return (
      <PageState
        title="Progreso"
        status="error"
        message={state.error}
        action={<button type="button" className="gym-button gym-button--secondary" onClick={load}>Reintentar</button>}
      />
    );
  }

  const { data } = state;
  const weekly = data.weekly || [];
  const currentWeek = weekly.length > 0 ? weekly[weekly.length - 1] : { trainingDays: 0, volumeKg: 0 };
  const personalRecords = data.personalRecords || [];
  const selectedRecord = personalRecords.find((record) => record.exerciseId === selectedExerciseId) || null;
  const hasTarget = typeof data.targetDaysPerWeek === 'number';

  return (
    <section className="gym-page" aria-labelledby="gym-page-title">
      <header className="gym-page__header">
        <h1 id="gym-page-title" className="gym-page__title">Progreso</h1>
      </header>

      <div className="gym-member-grid gym-progress-overview">
        <article className="gym-card gym-progress-overview__featured">
          <h2 className="gym-card__title">Días esta semana</h2>
          {hasTarget ? (
            <div className="gym-progress-overview__featured-metric">
              <p className="gym-progress-stat">
                {currentWeek.trainingDays}
                <span className="gym-progress-stat__target">/{data.targetDaysPerWeek}</span>
              </p>
              <span
                className="gym-progress-ring"
                aria-hidden="true"
                style={{ '--gym-progress-ring-value': `${Math.min(100, (currentWeek.trainingDays / data.targetDaysPerWeek) * 100)}%` }}
              />
            </div>
          ) : (
            <>
              <p className="gym-progress-stat gym-progress-stat--text">{currentWeek.trainingDays} días · sin objetivo</p>
              <p className="gym-progress-target-hint">
                Cargá tu objetivo semanal en <Link to="/mi-cuenta/perfil">Mi perfil</Link>.
              </p>
            </>
          )}
        </article>

        <article className="gym-card gym-progress-overview__visits">
          <h2 className="gym-card__title">Visitas al gimnasio</h2>
          <p className="gym-progress-stat">{currentWeek.attendanceDays ?? 0}</p>
        </article>

        <article className="gym-card gym-progress-overview__sessions">
          <h2 className="gym-card__title">Sesiones totales</h2>
          <p className="gym-progress-stat">{data.sessionsCount}</p>
        </article>

        <article className="gym-card gym-progress-overview__volume">
          <h2 className="gym-card__title">Volumen semana</h2>
          <p className="gym-progress-stat">{formatKg(currentWeek.volumeKg)}</p>
        </article>
      </div>

      <div className="gym-progress-charts">
        <article className="gym-progress-chart-card">
          <BarChart
            title="Días entrenados por semana"
            unit="días"
            target={hasTarget ? data.targetDaysPerWeek : undefined}
            data={weekly.map((week) => ({ label: shortDate(week.weekStart), value: week.trainingDays }))}
          />
        </article>
        <article className="gym-progress-chart-card">
          <BarChart
            title="Volumen semanal"
            unit="kg"
            data={weekly.map((week) => ({ label: shortDate(week.weekStart), value: week.volumeKg }))}
          />
        </article>
      </div>

      <section className="gym-progress-records" aria-labelledby="gym-pr-title">
        <h2 id="gym-pr-title" className="gym-card__title">Marcas personales</h2>
        {personalRecords.length === 0 ? (
          <p className="gym-state__text">Todavía no tenés marcas registradas.</p>
        ) : (
          <ul className="gym-pr-list">
            {personalRecords.map((record) => (
              <li key={record.exerciseId}>
                <button
                  type="button"
                  className={`gym-pr-item__button${record.exerciseId === selectedExerciseId ? ' is-active' : ''}`}
                  aria-pressed={record.exerciseId === selectedExerciseId}
                  onClick={() => selectExercise(record.exerciseId)}
                >
                  <span className="gym-pr-item__name">{record.exerciseName}</span>
                  <span className="gym-pr-item__value">{formatKg(record.bestWeightKg)}</span>
                  <span className="gym-pr-item__date">{formatDate(record.performedOn)}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {selectedExerciseId && (
        <section className="gym-progress-exercise" aria-live="polite">
          {exerciseState.status === 'loading' && <p role="status">Cargando progreso…</p>}
          {exerciseState.status === 'error' && <p role="alert" className="gym-form__error">{exerciseState.error}</p>}
          {exerciseState.status === 'loaded' && (
            <>
              <LineChart
                title={`${selectedRecord ? selectedRecord.exerciseName : 'Ejercicio'} — peso máximo y 1RM estimado`}
                unit="kg"
                series={[
                  {
                    name: 'Peso máximo',
                    points: exerciseState.data.points.map((point, index) => ({ x: index, y: point.maxWeightKg, label: shortDate(point.performedOn) })),
                  },
                  {
                    name: '1RM estimado',
                    points: exerciseState.data.points.map((point, index) => ({ x: index, y: point.estimated1RmKg, label: shortDate(point.performedOn) })),
                  },
                ]}
              />
              <div className="gym-progress-exercise__legend">
                <span className="gym-progress-exercise__legend-item gym-progress-exercise__legend-item--max">
                  <span className="gym-progress-exercise__legend-key" aria-hidden="true" />Peso máximo
                </span>
                <span className="gym-progress-exercise__legend-item gym-progress-exercise__legend-item--estimated">
                  <span className="gym-progress-exercise__legend-key" aria-hidden="true" />1RM estimado
                </span>
              </div>
              {exerciseState.data.best && (
                <p>Mejor marca: {formatKg(exerciseState.data.best.weightKg)} ({formatDate(exerciseState.data.best.performedOn)})</p>
              )}
              {exerciseState.data.last ? (
                <div>
                  <p>Última vez: {formatDate(exerciseState.data.last.performedOn)}</p>
                  <ul className="gym-progress-exercise__sets">
                    {exerciseState.data.last.sets.map((set) => (
                      <li key={set.setNumber}>Serie {set.setNumber}: {set.reps} × {formatKg(set.weightKg)}</li>
                    ))}
                  </ul>
                </div>
              ) : (
                <p>Todavía no hay historial para este ejercicio.</p>
              )}
            </>
          )}
        </section>
      )}
    </section>
  );
}
